import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { LLAppointmentCard } from './LLAppointmentCard';
import { LLAppointmentFilters } from './LLAppointmentFilters';

export const LLAdminAppointmentsView = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    date: ''
  });

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();
      setAppointments(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleStatusChange = async (id, status) => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      setSuccessMessage('Appointment status updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchAppointments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete appointment');
      }

      setSuccessMessage('Appointment deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchAppointments();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
        <h1 className="text-2xl font-bold text-white mb-6">Appointments</h1>
        <LLAppointmentFilters 
          filters={filters} 
          onFilterChange={handleFilterChange} 
        />
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 text-green-400">
          {successMessage}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-400">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Appointments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appointments.map(appointment => (
              <LLAppointmentCard
                key={appointment._id}
                appointment={appointment}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Empty State */}
          {appointments.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400">
              No appointments found
            </div>
          )}
        </>
      )}
    </div>
  );
};